import type { GroupBase } from 'react-select';

import type { SelectProps } from '#select/types/select.props';

import { useIsFixed } from '#select/hooks/is-fixed.hook';

/**
 * Custom hook that provides configuration for a Select component,
 * handling logic related to fixed options and clearability.
 *
 * @template IsMulti - Indicates if the select is multi-select.
 * @template Option - The type of the select options.
 * @template Group - The type of the option groups.
 *
 * @param params - Configuration parameters for the select.
 * @param params.isClearable - Whether the select can be cleared.
 * @param params.isFixed - Whether the selected option(s) are fixed.
 * @param params.onChange - Callback fired when the selection changes.
 * @param params.options - The available options for selection.
 * @param params.value - The current value of the select.
 *
 * @returns An object containing:
 * - `changed`: Indicates if the selection has changed.
 * - `clearable`: Indicates if the select is clearable based on fixed options.
 */
export const useSelectConfiguration = <
  IsMulti extends boolean = false,
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  isClearable,
  isFixed,
  onChange,
  options,
  value,
}: Pick<SelectProps<IsMulti, Option, Group>, 'isClearable' | 'isFixed' | 'onChange' | 'options' | 'value'>) => {
  const { changed, clearable } = useIsFixed<IsMulti, Option, Group>({ isClearable, isFixed, onChange, options, value });

  return { changed, clearable };
};
